import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useCurrentOrg,
  useDeleteOrg,
  useMe,
  useUpdateOrg,
} from "@/lib/queries";
import { ApiError, setActiveOrgSlug } from "@/lib/api";
import { cn } from "@/lib/cn";

// Common North-American zones surface to the top of the picker; the full
// IANA list (via Intl.supportedValuesOf) drops below for everything else.
const POPULAR_ZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
] as const;

function allTimezones(): string[] {
  const intl = Intl as unknown as {
    supportedValuesOf?: (kind: "timeZone") => string[];
  };
  if (typeof intl.supportedValuesOf === "function") {
    try {
      return intl.supportedValuesOf("timeZone");
    } catch {
      // fall through
    }
  }
  return [...POPULAR_ZONES];
}

export default function OrgSettings() {
  const { data: org, isLoading } = useCurrentOrg();
  const { data: me } = useMe();
  const update = useUpdateOrg();
  const del = useDeleteOrg();
  const [, setLocation] = useLocation();

  const myRole = useMemo(() => {
    if (!me || !org) return null;
    return me.orgs.find((o) => o.orgId === org.id)?.role ?? null;
  }, [me, org]);
  const canEdit =
    me?.isPlatformAdmin || myRole === "owner" || myRole === "admin";
  const isOwner = me?.isPlatformAdmin || myRole === "owner";

  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed form from server state once it loads.
  useEffect(() => {
    if (org) {
      setName(org.name);
      setTimezone(org.timezone);
    }
  }, [org]);

  const dirty =
    !!org && (name !== org.name || timezone !== org.timezone);

  async function onSave() {
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync({ name, timezone });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError((e as ApiError).message ?? "Could not save");
    }
  }

  if (isLoading || !org) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-2xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
          Org settings
        </h1>
        <p className="text-muted-foreground text-sm">
          {canEdit ? "Configure how this org runs." : "Read-only — only owners and admins can change settings."}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>General</CardTitle>
          <CardDescription>
            Name shows in spectator links, the URL slug is{" "}
            <code className="bg-muted px-1 py-0.5 rounded">/{org.slug}</code>.
            Slugs aren't editable here yet — ping support if you need a change.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canEdit}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={!canEdit}
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <optgroup label="Common">
                {POPULAR_ZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </optgroup>
              <optgroup label="All zones">
                {allTimezones()
                  .filter((tz) => !POPULAR_ZONES.includes(tz as (typeof POPULAR_ZONES)[number]))
                  .map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
              </optgroup>
            </select>
            <p className="text-xs text-muted-foreground">
              Used for scheduling events and the "today's games" filter.
            </p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex items-center justify-end gap-3 pt-2">
            {saved && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Check size={14} /> Saved
              </span>
            )}
            <Button
              onClick={onSave}
              disabled={!canEdit || !dirty || update.isPending}
            >
              {update.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Saving…
                </>
              ) : (
                "Save changes"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isOwner && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Danger zone</CardTitle>
            <CardDescription>
              Deleting permanently removes the org and everything in it —
              players, games, events, tournaments. There's no undo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog.Root>
              <Dialog.Trigger asChild>
                <Button variant="destructive">
                  <AlertTriangle size={16} />
                  Delete this org
                </Button>
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
                <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-xl">
                  <Dialog.Title className="font-display uppercase tracking-tight text-xl">
                    Delete {org.name}?
                  </Dialog.Title>
                  <Dialog.Description className="text-sm text-muted-foreground mt-2">
                    Type the org slug{" "}
                    <code className="bg-muted px-1 py-0.5 rounded">{org.slug}</code>{" "}
                    to confirm. This will remove all members, players, games,
                    events, and tournaments owned by this org.
                  </Dialog.Description>
                  <ConfirmDelete
                    expected={org.slug}
                    pending={del.isPending}
                    onConfirm={async () => {
                      try {
                        await del.mutateAsync();
                        // Drop the deleted org from local state, switch to
                        // whatever's next (or onboarding if nothing else).
                        const remaining = me?.orgs.filter(
                          (o) => o.orgId !== org.id,
                        );
                        const next = remaining?.[0]?.orgSlug ?? null;
                        if (next) {
                          localStorage.setItem("activeOrgSlug", next);
                          setActiveOrgSlug(next);
                          setLocation("/dashboard");
                        } else {
                          localStorage.removeItem("activeOrgSlug");
                          setActiveOrgSlug(null);
                          setLocation("/dashboard/orgs/new");
                        }
                      } catch (e) {
                        setError(
                          (e as ApiError).message ?? "Could not delete org",
                        );
                      }
                    }}
                  />
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ConfirmDelete({
  expected,
  pending,
  onConfirm,
}: {
  expected: string;
  pending: boolean;
  onConfirm: () => void;
}) {
  const [input, setInput] = useState("");
  const matches = input.trim() === expected;
  return (
    <div className="space-y-4 mt-5">
      <Input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={expected}
        autoFocus
      />
      <div className="flex justify-end gap-2">
        <Dialog.Close asChild>
          <Button variant="outline">Cancel</Button>
        </Dialog.Close>
        <Button
          variant="destructive"
          disabled={!matches || pending}
          onClick={onConfirm}
        >
          {pending ? "Deleting…" : "Delete org"}
        </Button>
      </div>
    </div>
  );
}
